import { describe, expect, it } from 'vitest';

import { CARD_CATEGORIES } from '../../../../../shared/cardConfig/constants';
import {
  ATTACHMENT_FIELDS,
  BILL_TO_ADDRESS_FIELDS,
  COMPANY_FIELDS,
  CONTACT_FIELDS,
  CONTACT_SAME_AS_FIELDS,
  PAYER_ADDRESS_FIELDS,
  SHIP_TO_ADDRESS_FIELDS,
  SOLD_TO_ADDRESS_FIELDS
} from '../../../../../shared/fieldConfig/constants';
import { PAGE_ROUTES } from '../../../../../utils/constants';
import type { Card, Field, Page } from './create';
import { templateMap } from './create';

describe('Customer Setup Template', () => {
  const { customer_set_up } = templateMap;
  const pages = customer_set_up.pages;

  it('exports pages array', () => {
    expect(Array.isArray(pages)).toBe(true);
    expect(pages.length).toBe(4); // company, shipTo, financial, review
  });

  const companyPage = pages.find(p => p.route === PAGE_ROUTES.company) as Page;
  const shipToPage = pages.find(p => p.route === PAGE_ROUTES.shipTo) as Page;
  const financialPage = pages.find(p => p.route === PAGE_ROUTES.financial) as Page;

  // ------------------ Company Page ------------------
  describe('Company Page', () => {
    it('has company, soldTo, and billTo cards', () => {
      const cardKeys = companyPage.cards!.map((c: Card) => c.key);
      expect(cardKeys).toEqual(
        expect.arrayContaining([
          CARD_CATEGORIES.company,
          CARD_CATEGORIES.soldTo,
          CARD_CATEGORIES.billTo
        ])
      );
    });

    it('company card fields', () => {
      const companyCard = companyPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.company) as Card;
      expect(companyCard.fields?.map((f: Field) => f.key)).toEqual([
        COMPANY_FIELDS.legalName,
        COMPANY_FIELDS.tradeDbaName
      ]);
    });

    it('soldTo card has required fields', () => {
      const soldToCard = companyPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.soldTo) as Card;
      const requiredFields = soldToCard.fields?.filter((f: Field) => f.required).map((f: Field) => f.key);
      expect(requiredFields).toEqual([
        SOLD_TO_ADDRESS_FIELDS.email,
        SOLD_TO_ADDRESS_FIELDS.icmsTaxPayer,
        SOLD_TO_ADDRESS_FIELDS.telephone1
      ]);
    });

    it('billTo card is optional', () => {
      const billToCard = companyPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.billTo) as Card;
      expect(billToCard.isCardOptional).toBe(true);
      expect(billToCard.fields?.map((f: Field) => f.key)).toEqual([
        BILL_TO_ADDRESS_FIELDS.address,
        BILL_TO_ADDRESS_FIELDS.taxNumber1
      ]);
    });
  });

  // ------------------ ShipTo Page ------------------
  describe('ShipTo Page', () => {
    it('has expected cards', () => {
      const keys = shipToPage.cards!.map((c: Card) => c.key);
      expect(keys).toEqual(
        expect.arrayContaining([
          CARD_CATEGORIES.address,
          CARD_CATEGORIES.deliveryDetails,
          CARD_CATEGORIES.deliveryInstructions,
          CARD_CATEGORIES.coa,
          CARD_CATEGORIES.orderConfirmation
        ])
      );
    });

    it('address card fields and wrapper', () => {
      const addressCard = shipToPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.address) as Card;
      expect(addressCard.fields?.map((f: Field) => f.key)).toEqual([
        SHIP_TO_ADDRESS_FIELDS.address,
        SHIP_TO_ADDRESS_FIELDS.taxNumber1
      ]);
      expect(addressCard.hideSameAsBillTo).toBe(true);
      expect(addressCard.fieldWrapper).toBe(CARD_CATEGORIES.shipTo);
    });

    it('COA card fields', () => {
      const coaCard = shipToPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.coa) as Card;
      expect(coaCard.fields?.map((f: Field) => f.key)).toEqual([CONTACT_FIELDS.email]);
      expect(coaCard.fieldWrapper).toBe(CARD_CATEGORIES.shipTo);
    });

    it('Order Confirmation card sameAsFields', () => {
      const orderCard = shipToPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.orderConfirmation) as Card;
      expect(orderCard.sameAsFields).toEqual([CONTACT_SAME_AS_FIELDS.sameAsCoa]);
    });
  });

  // ------------------ Financial Page ------------------
  describe('Financial Page', () => {
    it('payer card fields', () => {
      const payerCard = financialPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.payer) as Card;
      expect(payerCard.fields?.map((f: Field) => f.key)).toEqual([
        PAYER_ADDRESS_FIELDS.address,
        PAYER_ADDRESS_FIELDS.taxNumber1,
        PAYER_ADDRESS_FIELDS.xmlEmail,
        PAYER_ADDRESS_FIELDS.paymentSlipEmail
      ]);
      expect(payerCard.hideSameAsBillTo).toBe(true);
    });

    it('contains attachment and privacy cards', () => {
      const attachmentCard = financialPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.additionalAttachmentCard) as Card;
      expect(attachmentCard.fields?.[0].key).toBe(ATTACHMENT_FIELDS.additionalAttachment);

      const privacyCard = financialPage.cards!.find((c: Card) => c.key === CARD_CATEGORIES.privacyPolicy) as Card;
      expect(privacyCard.fields).toBeUndefined();
    });
  });

});
